import { useCallback } from 'react';
import axiosInstance from '../axiosInstance';

/**
 * Custom hook for security-related functionality
 */
export const useSecurity = () => {
  /**
   * Check security status before performing sensitive operations
   */
  const checkSecurityStatus = useCallback(async () => {
    try {
      // Check if we're in a secure context (HTTPS)
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        return {
          safe: false,
          message: 'This application requires a secure connection (HTTPS)'
        };
      }

      // Check for suspicious browser features
      if (navigator.webdriver) {
        return {
          safe: false,
          message: 'Automated browsers are not allowed'
        };
      }

      // Check for required browser features
      if (!window.crypto || !window.crypto.subtle) {
        return {
          safe: false,
          message: 'Your browser does not support required security features'
        };
      }

      // Check for suspicious extensions
      if (window.chrome && window.chrome.runtime && window.chrome.runtime.id) {
        // This is a Chrome extension context, which might be suspicious
        console.warn('Running in Chrome extension context');
      }

      return { safe: true };
    } catch (error) {
      console.error('Security check failed:', error);
      return { safe: true }; // Allow if check fails
    }
  }, []);

  /**
   * Record security events for monitoring
   */
  const recordSecurityEvent = useCallback(async (eventType, data = {}) => {
    try {
      const eventData = {
        event_type: eventType,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...data
      };

      // Send to backend for logging
      await axiosInstance.post('/security/events', eventData, {
        timeout: 5000
      }).catch(error => {
        console.warn('Failed to record security event:', error);
      });

    } catch (error) {
      console.error('Error recording security event:', error);
    }
  }, []);

  /**
   * Check for suspicious activity patterns
   */
  const detectSuspiciousActivity = useCallback(() => {
    const suspiciousPatterns = [];

    // Check for rapid clicks (potential bot behavior)
    const clickTimes = JSON.parse(sessionStorage.getItem('click_times') || '[]');
    const now = Date.now();
    clickTimes.push(now);
    
    // Keep only last 10 clicks
    const recentClicks = clickTimes.slice(-10);
    sessionStorage.setItem('click_times', JSON.stringify(recentClicks));
    
    if (recentClicks.length >= 5) {
      const timeDiff = recentClicks[recentClicks.length - 1] - recentClicks[0];
      if (timeDiff < 1000) { // 5 clicks in less than 1 second
        suspiciousPatterns.push('rapid_clicking');
      }
    }

    // Check for missing mouse movements (potential automation)
    const mouseMovements = JSON.parse(sessionStorage.getItem('mouse_movements') || '[]');
    if (mouseMovements.length === 0 && recentClicks.length > 0) {
      suspiciousPatterns.push('no_mouse_movement');
    }

    return suspiciousPatterns;
  }, []);

  /**
   * Initialize security monitoring
   */
  const initializeSecurityMonitoring = useCallback(() => {
    // Track mouse movements
    const trackMouseMovement = () => {
      const movements = JSON.parse(sessionStorage.getItem('mouse_movements') || '[]');
      movements.push(Date.now());
      
      // Keep only last 50 movements
      const recentMovements = movements.slice(-50);
      sessionStorage.setItem('mouse_movements', JSON.stringify(recentMovements));
    };

    // Track keyboard activity
    const trackKeyboardActivity = () => {
      const keystrokes = JSON.parse(sessionStorage.getItem('keystrokes') || '[]');
      keystrokes.push(Date.now());
      
      // Keep only last 100 keystrokes
      const recentKeystrokes = keystrokes.slice(-100);
      sessionStorage.setItem('keystrokes', JSON.stringify(recentKeystrokes));
    };

    // Add event listeners
    document.addEventListener('mousemove', trackMouseMovement, { passive: true });
    document.addEventListener('keydown', trackKeyboardActivity, { passive: true });

    // Cleanup function
    return () => {
      document.removeEventListener('mousemove', trackMouseMovement);
      document.removeEventListener('keydown', trackKeyboardActivity);
    };
  }, []);

  /**
   * Validate request headers for security
   */
  const validateRequestHeaders = useCallback((headers) => {
    const requiredHeaders = [
      'Content-Type',
      'X-Requested-With'
    ];

    const missingHeaders = requiredHeaders.filter(header => !headers[header]);
    
    if (missingHeaders.length > 0) {
      console.warn('Missing required headers:', missingHeaders);
      return false;
    }

    return true;
  }, []);

  /**
   * Generate secure random string
   */
  const generateSecureRandom = useCallback((length = 32) => {
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }, []);

  return {
    checkSecurityStatus,
    recordSecurityEvent,
    detectSuspiciousActivity,
    initializeSecurityMonitoring,
    validateRequestHeaders,
    generateSecureRandom
  };
};
